import React, { useState, useCallback, useMemo } from 'react';
import { 
  Stack, 
  TextInput, 
  Group, 
  Chip, 
  Text, 
  Loader,
  Paper,
  ActionIcon,
  Box,
  Button,
  useMantineTheme,
  useComputedColorScheme
} from '@mantine/core';
import { IconCheck, IconHelpOctagon, IconSend } from '@tabler/icons-react';
import { MenuItem, MenuItemFaqPayload } from '../../types/menu';
import api from '../../services/api';
import { t } from '../../utils/uiTranslations';
import { getClaritySessionUrl } from '../../utils/analytics';

interface MenuItemQuestionProps {
  menuItem: MenuItem;
  /** Menu the item belongs to; questions go to its /query/menu-item endpoint. Optional with askQuestion. */
  menuId?: string;
  restaurantName?: string;
  restaurantDescription?: string;
  currentLanguage?: string;
  faq?: MenuItemFaqPayload;
  /** Display name of the dish (English), used to synthesize the first "What is [name]?" chip */
  displayName?: string;
  /**
   * Answers a question instead of the menu's endpoint (e.g. the scan page, whose items
   * may have no menu). When set, cached FAQ answers aren't logged against a menu either.
   */
  askQuestion?: (question: string) => Promise<string>;
}

/**
 * Component for asking questions about a specific menu item.
 * Features:
 * - Text input field for custom questions
 * - Chips with suggested questions that can be clicked
 * - AI-powered answers displayed below the input and above the chips
 */
export const MenuItemQuestion: React.FC<MenuItemQuestionProps> = ({
  menuItem,
  menuId,
  restaurantName,
  restaurantDescription,
  currentLanguage = 'en',
  faq,
  displayName,
  askQuestion
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPregeneratedAnswer, setIsPregeneratedAnswer] = useState(false);
  /** Tracks which suggested question chip is highlighted (matches the current answer). Null when answer is from a custom typed question. */
  const [highlightedQuestion, setHighlightedQuestion] = useState<string | null>(null);

  // Default suggested questions (used when no FAQ data or 0 FAQ entries).
  // FAQ generation can return 0 entries when the menu item lacks enough content (e.g. "mixed nuts" with no description).
  // These are language-aware so the UI feels consistent when switching language.
  // When displayName is provided (v2 only), "What is [dish]?" replaces the first default question.
  const defaultSuggestedQuestions = currentLanguage === 'jp'
    ? ['この料理で特に感じる味わいは？', 'この料理の食感はどんな感じ？', 'これは辛い？', '何人前ですか？']
    : ['What flavors are most prominent in this?', 'How is the texture of this?', 'Is this spicy?', 'How many does this serve?'];

  // Use FAQ questions when we have at least one; otherwise surface default questions (clicks trigger API call).
  const faqEntries = useMemo(() => faq?.entries ?? [], [faq]);
  const hasFaqQuestions = faqEntries.length > 0;

  // "What is [dish]?" is always the first chip when displayName is provided (v2 menu view only).
  const whatIsQuestion = displayName ? `What is ${displayName}?` : null;

  const suggestedQuestions = hasFaqQuestions
    ? [
        ...(whatIsQuestion ? [whatIsQuestion] : []),
        ...faqEntries.map(entry => entry.question)
      ]
    : whatIsQuestion
      ? [whatIsQuestion, ...defaultSuggestedQuestions.slice(1)]
      : defaultSuggestedQuestions;

  /**
   * Sends the question to the AI service and displays the answer.
   * Uses the menu item specific query endpoint that only uses this menu item's data.
   * This is called for text field submissions and default question chips.
   */
  const handleSubmitQuestion = useCallback(async (questionText: string) => {
    if (!questionText.trim() || isLoading) return;

    setIsLoading(true);
    setError(null);
    setAnswer(null);
    setIsPregeneratedAnswer(false);

    try {
      let aiAnswer: string;
      if (askQuestion) {
        aiAnswer = (await askQuestion(questionText)) || t('noAnswerAvailable', currentLanguage);
      } else {
        // Make API call to the menu item specific query endpoint
        // This endpoint only uses the specific menu item data, not the whole menu
        const response = await api.post('/query/menu-item', {
          user_query: questionText,
          menu_id: menuId,
          menu_item_id: menuItem.id,
          current_language: currentLanguage,
          clarity_session_url: getClaritySessionUrl()
        });

        // Extract the answer from the response
        aiAnswer = response.data.message || t('noAnswerAvailable', currentLanguage);
      }
      setAnswer(aiAnswer);
      setIsPregeneratedAnswer(false);
    } catch (err) {
      console.error('Error fetching answer:', err);
      setError(t('questionError', currentLanguage));
      setAnswer(null);
      setIsPregeneratedAnswer(false);
    } finally {
      setIsLoading(false);
    }
  }, [menuItem.id, menuId, currentLanguage, isLoading, askQuestion]);

  /**
   * Handles form submission when user presses Enter or clicks submit.
   * Typed questions are custom, so no chip is highlighted.
   */
  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (question.trim()) {
      setHighlightedQuestion(null);
      handleSubmitQuestion(question);
    }
  }, [question, handleSubmitQuestion]);

  /**
   * Handles clicking on a suggested question chip.
   * Highlights this chip and clears any previous chip highlight.
   * If it's a pre-generated FAQ question, show the pre-generated answer.
   * Otherwise, make an API call for default questions.
   */
  const handleChipClick = useCallback((suggestedQuestion: string) => {
    setHighlightedQuestion(suggestedQuestion);
    setQuestion(suggestedQuestion);

    // Check if this is a pre-generated FAQ question (exact match)
    if (hasFaqQuestions) {
      const faqEntry = faqEntries.find(entry => entry.question === suggestedQuestion);
      if (faqEntry) {
        // Use pre-generated answer - no AI call needed, but log the interaction
        setAnswer(faqEntry.answer);
        setIsPregeneratedAnswer(true);
        setError(null);
        if (askQuestion || !menuId) return;
        api.post('/query/log', {
          user_query: suggestedQuestion,
          answer: faqEntry.answer,
          menu_id: menuId,
          menu_item_id: menuItem.id,
          source: 'faq_cached',
          clarity_session_url: getClaritySessionUrl()
        }).catch(() => {/* non-critical */});
        return;
      }
    }

    // "What is [dish]?" chip and default questions always make an API call
    setIsPregeneratedAnswer(false);
    handleSubmitQuestion(suggestedQuestion);
  }, [hasFaqQuestions, faqEntries, handleSubmitQuestion, askQuestion, menuId, menuItem.id]);

  /**
   * Clears the current question and answer
   */
  const handleClear = useCallback(() => {
    setQuestion('');
    setAnswer(null);
    setError(null);
    setIsPregeneratedAnswer(false);
    setHighlightedQuestion(null);
  }, []);

  /**
   * Handles focus on the text input field
   * If an answer is already displayed, clear the input field
   */
  const handleInputFocus = useCallback(() => {
    if (answer) {
      setQuestion('');
    }
  }, [answer]);

  return (
    <Stack gap="md" style={{ marginTop: '16px' }}>
      {/* Section Title */}
      <Group gap="xs" align="center">
        <IconHelpOctagon size={18} style={{ color: 'var(--mantine-color-orange-6)' }} />
        <Text size="sm" fw={600}>
          {t('askAboutThisItem', currentLanguage)}
        </Text>
      </Group>

      {/* Text Input Field */}
      <form onSubmit={handleFormSubmit}>
        <TextInput
          placeholder={t('askAboutMenuItemPlaceholder', currentLanguage)}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onFocus={handleInputFocus}
          disabled={isLoading}
          rightSection={
            <ActionIcon
              variant="filled"
              color="orange"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleFormSubmit(e as any);
              }}
              disabled={isLoading || !question.trim()}
              aria-label={t('send', currentLanguage)}
              type="submit"
            >
              <IconSend size={16} />
            </ActionIcon>
          }
          styles={{
            input: {
              borderRadius: '8px',
              fontSize: 16
            }
          }}
        />
      </form>

      {/* Loading Indicator */}
      {isLoading && (
        <Group justify="center" gap="xs">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">{t('gettingAnswer', currentLanguage)}</Text>
        </Group>
      )}

      {/* Error Message */}
      {error && (
        <Text size="sm" c="red">
          {error}
        </Text>
      )}

      {/* AI Answer Display - Shows below input and above chips */}
      {answer && !isLoading && (
        <Paper
          p="md"
          radius="md"
          style={{
            backgroundColor: isDarkMode ? theme.colors.dark[7] : theme.colors.gray[0],
            border: `1px solid ${isDarkMode ? theme.colors.dark[4] : theme.colors.gray[2]}`
          }}
        >
          <Stack gap="xs">
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {answer}
            </Text>
            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
              {t('createdByAi', currentLanguage)}
            </Text>
          </Stack>
        </Paper>
      )}

      {/* Suggested Question Chips - Display below answer if present, otherwise below input */}
      <Box>
        <Text size="xs" c="dimmed" mb="xs">
          {t('peopleAlsoAsk', currentLanguage)}
        </Text>
        <Group gap="xs">
          {suggestedQuestions.map((suggestedQuestion, index) => {
            const isHighlighted = suggestedQuestion === highlightedQuestion;
            // For FAQ questions, use Button instead of Chip to avoid toggle behavior
            // Button shows highlighted state when this question's answer is displayed
            if (hasFaqQuestions) {
              return (
                <Button
                  key={index}
                  onClick={() => handleChipClick(suggestedQuestion)}
                  disabled={isLoading}
                  variant={isHighlighted ? 'filled' : 'light'}
                  color={isHighlighted ? 'orange' : undefined}
                  size="xs"
                  leftSection={isHighlighted ? <IconCheck size={14} /> : undefined}
                  style={{
                    cursor: 'pointer',
                    height: 'auto',
                    padding: '4px 12px',
                    fontSize: 'var(--mantine-font-size-xs)'
                  }}
                >
                  {suggestedQuestion}
                </Button>
              );
            }
            // For default questions, use Chip (they make API calls). checked = highlighted state only.
            return (
              <Chip
                key={index}
                value={suggestedQuestion}
                checked={isHighlighted}
                onClick={() => handleChipClick(suggestedQuestion)}
                disabled={isLoading}
                variant="light"
                size="sm"
                color="orange"
                style={{
                  cursor: 'pointer'
                }}
              >
                {suggestedQuestion}
              </Chip>
            );
          })}
        </Group>
      </Box>
    </Stack>
  );
};

